import React from 'react'
import {
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  Snackbar,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material'
import { IStockCard, StockCard } from 'interfaces/IStockCard'
import { IReduxState } from 'interfaces/IReduxState'
import { useSelector } from 'react-redux'
import API from '@aws-amplify/api'
import {
  STOCK_CARD_TYPE_INGREDIENTS,
  VALUE_GUDANG_MIXING,
  VALUE_IN,
  VALUE_PURCHASING,
} from 'enums/stockCard'
import MuiAlert from '@mui/material/Alert'
import LoadIngredientAutocomplete from 'shared/autocomplete/loadIngredientAutocomplete'
import { IIngredient } from 'interfaces/IIngredient'

export default function IncomingIngredientStockCard(props: {}) {
  const user = useSelector((state: IReduxState) => state.currentUser || {})

  const [stockCardData, setStockCardData] = React.useState<IStockCard>(
    new StockCard({
      inOutDirection: VALUE_IN,
      type: STOCK_CARD_TYPE_INGREDIENTS,
    })
  )
  const [alertMessage, setAlertMessage] = React.useState('')
  const [isFailureSnackbarOpen, setIsFailureSnackbarOpen] =
    React.useState(false)
  const [isSuccessSnackbarOpen, setIsSuccessSnackbarOpen] =
    React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  const [ingredientData, setIngredientData] =
    React.useState<IIngredient | null>(null)

  const [ingredientIncomingFrom, setIngredientIncomingFrom] = React.useState('')

  const handleSnackbarClose = (): void => {
    setIsFailureSnackbarOpen(false)
    setIsSuccessSnackbarOpen(false)
  }

  const getValidationErrors = () => {
    const errors: string[] = []
    if (!stockCardData.ingredientId) errors.push('Bahan Baku wajib dipilih')
    if (!stockCardData.quantity || Number(stockCardData.quantity) <= 0) errors.push('Quantity Masuk harus > 0')
    if (!ingredientIncomingFrom) errors.push('Asal Barang wajib dipilih')

    if (ingredientIncomingFrom === VALUE_PURCHASING) {
      if (!stockCardData.purchaseOrderNumber) errors.push('No. PO wajib diisi')
      if (!stockCardData.expectedQuantity || Number(stockCardData.expectedQuantity) <= 0) {
        errors.push('Qty Expected harus > 0')
      }
    }
    return errors
  }

  const isAllFieldsComplete = () => {
    return getValidationErrors().length === 0
  }

  const saveStockCard = () => {
    setIsLoading(true)

    API.post('products', `/premix/createStockCard`, {
      body: {
        ...stockCardData,
        ingredientId: ingredientData?.ingredientId,
        type: STOCK_CARD_TYPE_INGREDIENTS,
        inOutDirection: VALUE_IN,
        outgoingDestination: ingredientIncomingFrom,
        isConfirmed: true,
      },
    })
      .then((response) => {
        setAlertMessage('Berhasil membuat kartu stok.')
        setIsSuccessSnackbarOpen(true)
        window.location.reload()
      })
      .catch((error) => {
        console.error(error)
        setAlertMessage('Gagal membuat kartu stok.')
        setIsFailureSnackbarOpen(true)
        console.error(error.response)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleStockCardFieldChange = (value: any, key: string): void => {
    setStockCardData((stockCardData) => ({ ...stockCardData, [key]: value }))
  }

  const handleQuantityChange = (value: string) => {
    setStockCardData((stockCardData) => ({
      ...stockCardData,
      quantity: value as any,
    }))
  }

  return (
    <React.Fragment>
      <Paper>
        <React.Fragment>
          <List>
            <ListItem>
              <LoadIngredientAutocomplete
                onChange={(e) => {
                  setIngredientData(e)
                  handleStockCardFieldChange(e.ingredientId, 'ingredientId')
                }}
              />
            </ListItem>
            <ListItem>
              <TextField
                autoComplete="off"
                variant="outlined"
                value={stockCardData.quantity}
                fullWidth
                label="Quantity Masuk (kg)"
                type="number"
                inputProps={{ step: '0.000001' }}
                onChange={(e) => handleQuantityChange(e.target.value)}
              />
            </ListItem>

            <ListItem>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  aria-label="position"
                  value={ingredientIncomingFrom}
                  onChange={(e) => setIngredientIncomingFrom(e.target.value)}
                >
                  <FormControlLabel
                    value={VALUE_GUDANG_MIXING}
                    control={<Radio color="primary" />}
                    label="Gudang Mixing"
                  />
                  <FormControlLabel
                    value={VALUE_PURCHASING}
                    control={<Radio color="primary" />}
                    label="Purchasing"
                  />
                </RadioGroup>
              </FormControl>
            </ListItem>
            {ingredientIncomingFrom === VALUE_PURCHASING && (
              <>
                <ListItem>
                  <TextField
                    autoComplete="off"
                    variant="outlined"
                    value={stockCardData.purchaseOrderNumber}
                    fullWidth
                    label="No. PO"
                    onChange={(e) =>
                      handleStockCardFieldChange(
                        e.target.value,
                        'purchaseOrderNumber'
                      )
                    }
                  />
                </ListItem>
                <ListItem>
                  <TextField
                    autoComplete="off"
                    variant="outlined"
                    value={stockCardData.expectedQuantity}
                    fullWidth
                    label="Qty Expected (kg)"
                    type="number"
                    inputProps={{ step: '0.000001' }}
                    onChange={(e) =>
                      handleStockCardFieldChange(
                        e.target.value,
                        'expectedQuantity'
                      )
                    }
                  />
                </ListItem>
              </>
            )}

            <ListItem>
              <TextField
                autoComplete="off"
                variant="outlined"
                value={stockCardData.notes}
                fullWidth
                label={'Notes'}
                onChange={(e) =>
                  handleStockCardFieldChange(e.target.value, 'notes')
                }
              />
            </ListItem>
            <ListItem>
              <Typography>Diterima Oleh: {user.username}</Typography>
            </ListItem>
          </List>
          <Box mt={3} />
          <Paper>
            <Box p={2} mt={2}>
              <Button
                variant="contained"
                color="primary"
                disabled={!isAllFieldsComplete() || isLoading}
                onClick={saveStockCard}
              >
                Save
              </Button>
              {!isAllFieldsComplete() && (
                <Box mt={1}>
                  {getValidationErrors().map((err) => (
                    <Typography key={err} variant="caption" color="error" display="block">
                      • {err}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          </Paper>
        </React.Fragment>{' '}
      </Paper>
      <Snackbar
        open={isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {alertMessage}
        </MuiAlert>
      </Snackbar>
    </React.Fragment>
  )
}
